import { Card, Container, Row, Col, Button } from "reactstrap";
import { keys as getKeys } from "underscore";
import { useParams } from "react-router-dom";
import { useState } from "react";

import CreateEditIndexDrawer from "../../../../components/Drawers/CreateEditIndexDrawer";
import useTenantModuleSupportal from "../../../../hooks/useTenantModuleSupportal";
import useGetIndexes from "../../../../api/api-hooks/modules/useGetIndexes";
import { BreadCrumSupportal } from "../TenantSchema/CloudTenantSchemaView";
import useCRUDIndex from "../../../../api/api-hooks/modules/useCRUDIndex";
import AppPageHeader from "../../../../components/Headers/AppPageHeader";
import AppHelmet from "../../../../components/AppComponents/AppHelmet";
import BasicTableView from "../../CloudListView/BasicTableView";
import useCloud from "../../../../api/api-hooks/cloud/useCloud";
import { Index } from "../../../../types/GlobalInterfaces";
import IconButton from "../../../../elements/IconButton";
import { AppConfirm } from "../../../../utils/PopupUtil";
import useToggle from "../../../../hooks/useToggle";
import Constants from "../../../../utils/Constants";

interface Indexes {
  showbreadcrumbs?: boolean;
  isSupportal?: boolean;
}

const ManageModuleIndexes = ({
  showbreadcrumbs = true,
  isSupportal = true,
}: Indexes) => {
  const { moduleAPIName, cloudID, APIName }: any = useParams();
  const finalModuleAPIName = moduleAPIName || APIName;

  const { module } = useTenantModuleSupportal({
    supportTargetCloudID: cloudID,
    moduleAPIName: finalModuleAPIName,
    isSupportal,
  });

  const { cloud, cloudLoading } = useCloud(cloudID);
  const [deleteIndex, setDelete] = useState("");
  const { indexes, indexesLoading } = useGetIndexes(
    finalModuleAPIName,
    cloudID,
    isSupportal
  );
  const [index, setIndex] = useState<Index | null>(null);
  const [open, setOpen] = useToggle();
  const { crudIndexAction } = useCRUDIndex();

  if (!module) {
    if (!isSupportal) {
      return <>Something went wrong</>;
    }

    return null;
  }

  return (
    <>
      <AppHelmet title="Clouds - GrowthCloud.com" />
      <div className="header pb-6">
        <Container fluid>
          <AppPageHeader
            title={`Manage ${module?.Name} Indexes `}
            description={`Manage your custom indexes to improve the speed for some of your queries.`}
            customContent={
              <>
                <Button
                  color="primary"
                  outline
                  onClick={() => {
                    setOpen();
                  }}
                >
                  Add Index
                </Button>
              </>
            }
            breadcrumbs={
              showbreadcrumbs && (
                <BreadCrumSupportal
                  loading={cloudLoading}
                  title={isSupportal ? cloud?.Name ?? "Cloud" : ""}
                  redirectTitle="Module"
                  redirectPath={
                    isSupportal
                      ? `/clouds/${Constants.SUP_CLOUD_ID}/supportal/clouds/${cloudID}/modules/${moduleAPIName}`
                      : ""
                  }
                />
              )
            }
          />
        </Container>
        <Container fluid>
          <Row>
            <Col>
              <Card>
                <BasicTableView
                  loading={indexesLoading}
                  items={indexes ?? []}
                  emptyMessage={"No indexes Found"}
                  title="Index"
                  getItemTitle={(i) => i.name}
                  getItemSubtitle={(i) => getFields(i.key)}
                  CustomRenderer={(i) => {
                    return (
                      <div style={{ width: 80 }}>
                        <IconButton
                          icon="pencil"
                          tooltipText="Edit Index"
                          onClick={() => {
                            setIndex(i);
                            setOpen();
                          }}
                        />
                        <IconButton
                          icon="close"
                          tooltipText="Delete Index"
                          onClick={() => {
                            setIndex(i);
                            setDelete(i.name);
                            onDeleteIndex(i);
                          }}
                          color="danger"
                          loading={i.name === deleteIndex}
                        />
                      </div>
                    );
                  }}
                />
              </Card>
            </Col>
          </Row>
          <CreateEditIndexDrawer
            open={open}
            handleClose={() => {
              setOpen();
              setIndex(null);
            }}
            module={module}
            tenantID={cloudID}
            existingIndex={index}
            isSupportal={isSupportal}
            key={`${moduleAPIName}${open}`}
          />
        </Container>
      </div>
    </>
  );

  function getFields(indexKey: { [key: string]: 1 | -1 }) {
    const keys = getKeys(indexKey);
    const asc: string[] = [];
    const desc: string[] = [];

    for (const key of keys || []) {
      if (indexKey[key] === 1) asc.push(key);
      else desc.push(key);
    }

    return (
      <>
        {asc?.length ? `(A-Z): ${asc.join(",")}.` : ``}
        {!!asc?.length && !!desc?.length && <br />}
        {desc?.length ? `(Z-A): ${desc.join(",")}.` : ``}
      </>
    );
  }

  async function onDeleteIndex(index: Index) {
    const result = await AppConfirm({
      title: `Delete '${index.name}' index?`,
      html: `Are you sure you want to delete the '${index.name}' index?`,
    });
    if (!result.isConfirmed) {
      setDelete("");
      return;
    }

    // @ts-ignore
    crudIndexAction({
      action: "Delete",
      moduleAPIName,
      isSupportal,
      tenantID: cloudID,
      data: index,
      onSuccess: () => {
        setIndex(null);
        setDelete("");
      },
      onError: () => {
        setDelete("");
      },
    });
  }
};

export default ManageModuleIndexes;
